// Animation engine of the Worki thinking indicator (WorkiThinking.tsx): three dots bounce while Worki thinks, then hop
// together between the six corners of Worki's hexagon while it works, then fade out. It draws straight into an empty
// <svg viewBox="0 0 20 20"> with requestAnimationFrame, so the animation never re-renders React. Colours come from
// the CSS variables --worki-thinking-dot (active) and --worki-thinking-dot-light (resting), set in worki.css.
// based on https://claude.ai/artifact/9PmwPoUzS9yfkc8NAj5rZ1?sk=ye-QSLCV1gbcj4nrDZ4_cQ

export type WorkiThinkingState = 'thinking' | 'working' | 'done';

export interface WorkiThinkingEngine {
    setState: (state: WorkiThinkingState) => void;
    destroy: () => void;
}

interface Ball {
    from: number;
    to: number;
    start: number;
    dur: number;
    pause: number;
    trail: [number, number, number][];
}

const NS = 'http://www.w3.org/2000/svg';
const CENTER = 10;
const RADIUS = 6.5;
const DOT_R = 2;
const BALL_R = 1.6;
const DOT_X = [5, 10, 15];
const DOT_Y = 12.5;
// start corners: left, top right, bottom right
const START = [3, 5, 1];
const THINKING_LOOP_MS = 1300;
const MORPH_MS = 380;
const FADE_MS = 260;
const LANDING_MS = 140;
const TAIL = 14;
const TAIL_MS = 170;

const CORNERS: [number, number][] = Array.from({ length: 6 }, (_, k) => [
    CENTER + RADIUS * Math.cos((k * Math.PI) / 3),
    CENTER + RADIUS * Math.sin((k * Math.PI) / 3),
]);

const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const clamp = (t: number) => Math.min(Math.max(t, 0), 1);
const color = (p: number) =>
    `color-mix(in srgb, var(--worki-thinking-dot) ${Math.round(p * 100)}%, var(--worki-thinking-dot-light))`;
const distance = (a: number, b: number) => Math.hypot(CORNERS[b][0] - CORNERS[a][0], CORNERS[b][1] - CORNERS[a][1]);

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const mountWorkiThinking = (
    svg: SVGSVGElement,
    { initialState = 'thinking', onDone }: { initialState?: WorkiThinkingState; onDone?: () => void } = {}
): WorkiThinkingEngine => {
    const reduced = prefersReducedMotion();
    const make = <K extends keyof SVGElementTagNameMap>(tag: K) =>
        svg.appendChild(document.createElementNS(NS, tag)) as SVGElementTagNameMap[K];
    const tails = [0, 1, 2].map(() => Array.from({ length: TAIL }, () => make('circle')));
    const dots = [0, 1, 2].map(() => make('ellipse'));
    const balls: Ball[] = START.map((corner) => ({ from: corner, to: corner, start: 0, dur: 0, pause: 0, trail: [] }));

    let state: WorkiThinkingState = initialState;
    let t0 = performance.now();
    // a state that starts out working has no bouncing dots to morph from: it starts on the hexagon
    let morphFrom: [number, number][] = START.map((corner) => CORNERS[corner]);
    let hopping = initialState !== 'thinking';
    let raf = 0;

    const draw = (
        el: SVGEllipseElement,
        x: number,
        y: number,
        rx: number,
        ry: number,
        mix: number,
        opacity: number,
        angle: number | null = null
    ) => {
        el.setAttribute('cx', String(x));
        el.setAttribute('cy', String(y));
        el.setAttribute('rx', String(Math.max(rx, 0)));
        el.setAttribute('ry', String(Math.max(ry, 0)));
        el.style.fill = color(mix);
        el.setAttribute('opacity', String(opacity));
        if (angle == null) el.removeAttribute('transform');
        else el.setAttribute('transform', `rotate(${angle} ${x} ${y})`);
    };

    const hideTails = () => tails.flat().forEach((circle) => circle.setAttribute('opacity', '0'));

    // all three leave at the same moment, each to a free random corner (never two on one corner)
    const hopAll = (now: number) => {
        balls.forEach((ball) => (ball.from = ball.to));
        const taken: number[] = [];
        balls.forEach((ball, i) => {
            const free = [0, 1, 2, 3, 4, 5].filter(
                (n) => n !== ball.from && !taken.includes(n) && !balls.some((other, j) => j > i && other.from === n)
            );
            ball.to = free[Math.floor(Math.random() * free.length)];
            taken.push(ball.to);
        });
        const longest = Math.max(...balls.map((ball) => distance(ball.from, ball.to)));
        const dur = 260 + longest * 20 + Math.random() * 60;
        const pause = Math.random() < 0.2 ? 260 + Math.random() * 240 : 80 + Math.random() * 80;
        balls.forEach((ball) => Object.assign(ball, { start: now, dur, pause }));
    };

    const drawHop = (now: number, fade: number) => {
        dots.forEach((dot, i) => {
            const ball = balls[i];
            const p = clamp((now - ball.start) / ball.dur);
            const [ax, ay] = CORNERS[ball.from];
            const [bx, by] = CORNERS[ball.to];
            const dx = bx - ax;
            const dy = by - ay;
            const len = Math.hypot(dx, dy) || 1;
            let nx = -dy / len;
            let ny = dx / len;
            // the arc always bends upward
            if (ny > 0 || (Math.abs(ny) < 0.01 && nx > 0)) {
                nx = -nx;
                ny = -ny;
            }
            const e = ease(p);
            const lift = Math.sin(Math.PI * p) * (1.2 + len * 0.22);
            const x = ax + dx * e + nx * lift;
            const y = ay + dy * e + ny * lift;

            ball.trail.unshift([x, y, now]);
            while (ball.trail.length && now - ball.trail[ball.trail.length - 1][2] > TAIL_MS) ball.trail.pop();

            // stretched along the flight, squashed for a moment on landing
            const landing = p >= 1 ? clamp(1 - (now - ball.start - ball.dur) / LANDING_MS) : 0;
            const stretch = Math.sin(Math.PI * p) * 0.22;
            const rx = p >= 1 ? BALL_R * (1 + 0.3 * landing) : BALL_R * (1 + stretch);
            const ry = p >= 1 ? BALL_R * (1 - 0.3 * landing) : BALL_R * (1 - stretch * 0.6);
            const angle = p > 0 && p < 1 ? (Math.atan2(dy, dx) * 180) / Math.PI : null;
            draw(dot, x, y, rx * fade, ry * fade, 1, fade, angle);

            tails[i].forEach((circle, k) => {
                const point = ball.trail[Math.floor((k * ball.trail.length) / TAIL)];
                if (!point || k === 0) {
                    circle.setAttribute('opacity', '0');
                    return;
                }
                const f = 1 - (now - point[2]) / TAIL_MS;
                circle.setAttribute('cx', String(point[0]));
                circle.setAttribute('cy', String(point[1]));
                circle.setAttribute('r', String(Math.max(0, BALL_R * 0.9 * f * fade)));
                // front 35 % of the trail in the active colour, the rest resting
                circle.style.fill = color(f > 0.65 ? 1 : 0);
                circle.setAttribute('opacity', String(Math.max(0, 0.55 * f * f * fade)));
            });
        });
    };

    const frame = (now: number) => {
        const t = now - t0;
        if (state === 'thinking') {
            dots.forEach((dot, i) => {
                let u = (t / THINKING_LOOP_MS - i * 0.1) % 1;
                if (u < 0) u += 1;
                const up = u < 0.25 ? ease(u / 0.25) : u < 0.55 ? 1 - ease((u - 0.25) / 0.3) : 0;
                draw(dot, DOT_X[i], DOT_Y - 4 * up, DOT_R, DOT_R, up, 1);
            });
        } else if (state === 'working' && !hopping && t < MORPH_MS) {
            // the bouncing dots arc onto three corners of the hexagon
            const m = ease(clamp(t / MORPH_MS));
            const lift = Math.sin(Math.PI * clamp(t / MORPH_MS)) * 2.5;
            dots.forEach((dot, i) => {
                const [ax, ay] = morphFrom[i];
                const [bx, by] = CORNERS[START[i]];
                const r = DOT_R + (BALL_R - DOT_R) * m;
                draw(dot, ax + (bx - ax) * m, ay + (by - ay) * m - lift, r, r, Math.max(m, 0.5), 1);
            });
        } else if (state === 'done' && !hopping) {
            // done straight from thinking: the three dots fade where they are
            const fade = clamp(1 - t / FADE_MS);
            dots.forEach((dot, i) =>
                draw(dot, morphFrom[i][0], morphFrom[i][1], DOT_R * fade, DOT_R * fade, 0.5, fade)
            );
        } else {
            if (!hopping || balls[0].dur === 0) {
                hopping = true;
                hopAll(now);
            }
            drawHop(now, state === 'done' ? clamp(1 - t / FADE_MS) : 1);
            if (state === 'working' && now - balls[0].start >= balls[0].dur + balls[0].pause) hopAll(now);
        }

        if (state === 'done' && t >= FADE_MS) {
            raf = 0;
            onDone?.();
            return;
        }
        raf = requestAnimationFrame(frame);
    };

    // reduced motion: the dots stand still (in a row while thinking, on the hexagon while working)
    const drawStatic = () => {
        hideTails();
        dots.forEach((dot, i) => {
            if (state === 'done') {
                dot.setAttribute('opacity', '0');
                return;
            }
            const [x, y] = state === 'thinking' ? [DOT_X[i], DOT_Y] : CORNERS[START[i]];
            const r = state === 'thinking' ? DOT_R : BALL_R;
            draw(dot, x, y, r, r, state === 'thinking' && i !== 1 ? 0.4 : 1, 1);
        });
        if (state === 'done') onDone?.();
    };

    const setState = (next: WorkiThinkingState) => {
        if (next === state) return;
        morphFrom = dots.map((dot) => [Number(dot.getAttribute('cx')), Number(dot.getAttribute('cy'))]);
        if (next === 'thinking') {
            hopping = false;
            balls.forEach((ball, i) => Object.assign(ball, { from: START[i], to: START[i], dur: 0, trail: [] }));
            hideTails();
        }
        state = next;
        t0 = performance.now();
        if (reduced) {
            drawStatic();
            return;
        }
        if (!raf) raf = requestAnimationFrame(frame);
    };

    if (reduced) drawStatic();
    else raf = requestAnimationFrame(frame);

    return {
        setState,
        destroy() {
            cancelAnimationFrame(raf);
            raf = 0;
            svg.replaceChildren();
        },
    };
};
